import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";

import MonthlyProjectionInsights, { MonthlyProjectionInvestigation } from "./MonthlyProjectionInsights";
import { COMPARISONS, computeDifference, describeToPayPeak, findToPayPeak } from "./monthlyProjectionInsightRules";
import {
  SERIES,
  buildMonthlyProjectionModel,
  formatBrl,
  formatMonthLabel,
} from "./monthlyProjectionModel";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const LEVEL_LABELS = {
  unit: "Unidade",
  cell: "Célula",
  step: "Etapa",
  substep: "Subetapa",
  service: "Serviço",
};

const cellText = (cell) => (cell?.status === "value" ? formatBrl(cell.value) : "—");

const availabilityNotes = (model) => SERIES
  .filter((series) => model.availability[series.key] !== "available")
  .map((series) => (
    model.availability[series.key] === "unavailable"
      ? `${series.label}: série não disponível na resposta atual.`
      : `${series.label}: nenhum ponto informado no período.`
  ));

const issuesNotice = (issues) => {
  const parts = [];
  if (issues.summary.invalid > 0) {
    parts.push(`${issues.summary.invalid} ${issues.summary.invalid === 1 ? "registro" : "registros"} com data ou valor inválido`);
  }
  if (issues.summary.outsideWindow > 0) {
    parts.push(`${issues.summary.outsideWindow} ${issues.summary.outsideWindow === 1 ? "registro" : "registros"} fora do período`);
  }
  if (issues.summary.duplicates > 0) {
    parts.push(`${issues.summary.duplicates} ${issues.summary.duplicates === 1 ? "registro duplicado" : "registros duplicados"}`);
  }
  if (parts.length === 0) return null;
  const total = issues.summary.invalid + issues.summary.outsideWindow + issues.summary.duplicates;
  return `${total} ${total === 1 ? "registro não pôde" : "registros não puderam"} ser exibido${total === 1 ? "" : "s"}: ${parts.join("; ")}. Os dados brutos não foram alterados.`;
};

const sourceLabel = (metadata) => {
  if (!metadata) return "Cronograma: fonte resolvida pela aplicação · Financeiro: processamento atual do Ampli";
  const source = metadata.source === "prevision" ? "Prevision" : (metadata.source ?? "fonte resolvida pela aplicação");
  return `Cronograma: ${source} · Financeiro: processamento atual do Ampli`;
};

const MonthlyProjectionComparison = ({
  series,
  window,
  loading,
  error,
  item,
  appropriationLevel,
  sourceMetadata,
  measurementReference,
  focusMonth,
  onFocusMonth,
  hiddenSeries,
  onToggleSeries,
  onFilterMonth,
  investigations,
  onInvestigate,
  now,
}) => {
  const [showDifferences, setShowDifferences] = useState(false);
  const model = useMemo(() => buildMonthlyProjectionModel({ series, window }), [series, window]);
  const focusKey = model.monthKeys.includes(focusMonth) ? focusMonth : model.monthKeys.at(-1) ?? null;
  const focusCells = focusKey ? model.monthsByKey[focusKey].values : null;
  const hidden = new Set(hiddenSeries ?? []);

  const chartData = useMemo(() => ({
    labels: model.months.map((month) => month.label),
    datasets: SERIES.map((definition) => ({
      label: definition.label,
      backgroundColor: definition.color,
      borderColor: definition.color,
      hidden: hidden.has(definition.key),
      data: model.months.map((month) => {
        const cell = month.values[definition.key];
        return cell.status === "value" ? cell.value : null;
      }),
    })),
  }), [model, hiddenSeries]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    onClick: (_event, elements) => {
      const index = elements?.[0]?.index;
      const key = model.monthKeys[index];
      if (key) onFocusMonth?.(key);
    },
    scales: {
      x: { stacked: false, title: { display: true, text: "Mês/Ano", font: { weight: "bold" } } },
      y: { stacked: false, title: { display: true, text: "Valor do mês (R$)", font: { weight: "bold" } } },
    },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => (items[0] ? model.months[items[0].dataIndex]?.longLabel : ""),
          label: (context) => `${context.dataset.label}: ${formatBrl(context.raw)}`,
          afterBody: () => ["Valor do mês, não acumulado."],
        },
      },
    },
  }), [model, onFocusMonth]);

  const notice = issuesNotice(model.issues);
  const unavailable = availabilityNotes(model);
  const typeLabel = item?.typeLabel ?? LEVEL_LABELS[item?.type] ?? "";
  const levelNote = appropriationLevel && item?.type && appropriationLevel !== item.type
    ? `Financeiro apropriado no nível ${LEVEL_LABELS[appropriationLevel] ?? appropriationLevel}; Pago e A pagar deste nível dependem de vínculo financeiro identificado e não são rateados entre atividades.`
    : null;

  const header = (
    <header className="monthly-projection-header">
      <div>
        <span className="monthly-projection-eyebrow">Projeção financeira · mês a mês</span>
        <h3>{item?.description ?? "Item da EAP"}</h3>
        <p className="monthly-projection-scope">
          {item?.wbs ? `${item.wbs} · ` : ""}{item?.wbs_code_id ?? ""}{typeLabel ? ` · ${typeLabel}` : ""}
          {model.window.begin
            ? ` · ${formatMonthLabel(model.window.begin)} a ${formatMonthLabel(model.window.end)}`
            : ""}
        </p>
        <p className="monthly-projection-sources">{sourceLabel(sourceMetadata)}</p>
        {levelNote && <p className="monthly-projection-sources">{levelNote}</p>}
      </div>
    </header>
  );

  if (loading) {
    return (
      <section className="monthly-projection">
        {header}
        <p className="schedule-callout" role="status">Carregando valores mensais do item…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="monthly-projection">
        {header}
        <p className="schedule-callout schedule-callout-error" role="alert">{error}</p>
      </section>
    );
  }

  return (
    <section className="monthly-projection">
      {header}

      <div className="monthly-projection-legend" role="group" aria-label="Séries do gráfico">
        {SERIES.map((definition) => (
          <button
            type="button"
            key={definition.key}
            className={hidden.has(definition.key) ? "hidden-series" : undefined}
            aria-pressed={!hidden.has(definition.key)}
            onClick={() => onToggleSeries?.(definition.key)}
          >
            <i aria-hidden="true" style={{ background: definition.color }} />
            {definition.label}
          </button>
        ))}
        <small>Ocultar uma série muda só a visualização; as diferenças usam sempre os cinco valores.</small>
      </div>

      {model.monthKeys.length === 0 ? (
        <p className="schedule-callout">Nenhum mês no período selecionado.</p>
      ) : (
        <div className="planned-measured-chart monthly-projection-chart">
          <Bar data={chartData} options={chartOptions} />
        </div>
      )}

      {(notice || unavailable.length > 0) && (
        <div className="schedule-callout schedule-callout-warning monthly-projection-notices">
          {notice && <p>{notice}</p>}
          {unavailable.map((text) => <p key={text}>{text}</p>)}
        </div>
      )}

      {focusKey && (
        <div className="monthly-projection-focus-controls">
          <label>
            <span>Mês em foco</span>
            <select
              aria-label="Mês em foco"
              value={focusKey}
              onChange={(event) => onFocusMonth?.(event.target.value)}
            >
              {model.months.map((month) => (
                <option key={month.key} value={month.key}>{month.longLabel}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="button button-accent small"
            onClick={() => onFilterMonth?.(focusKey)}
          >
            Filtrar este mês
          </button>
          <small>Selecionar um mês muda só a leitura abaixo. O período do gráfico só muda por esta ação.</small>
        </div>
      )}

      {focusKey && (
        <MonthlyProjectionInsights
          monthKey={focusKey}
          cells={focusCells}
          availability={model.availability}
          now={now}
          measurementReference={measurementReference}
        />
      )}

      {model.monthKeys.length > 0 && (
        <section className="monthly-projection-table-section">
          <div className="monthly-projection-table-header">
            <h4>Valores por mês</h4>
            <button
              type="button"
              className="button small"
              aria-pressed={showDifferences}
              onClick={() => setShowDifferences((current) => !current)}
            >
              {showDifferences ? "Ocultar diferenças" : "Mostrar diferenças"}
            </button>
          </div>
          <div className="monthly-projection-table-wrap">
            <table className="monthly-projection-table" aria-label="Valores por mês">
              <thead>
                <tr>
                  <th scope="col">Mês</th>
                  {SERIES.map((definition) => (
                    <th scope="col" key={definition.key}>{definition.label}</th>
                  ))}
                  {showDifferences && COMPARISONS.map((comparison) => (
                    <th scope="col" key={comparison.key}>{comparison.formula}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {model.months.map((month) => (
                  <tr key={month.key} className={month.key === focusKey ? "selected" : undefined} aria-selected={month.key === focusKey}>
                    <td>
                      <button
                        type="button"
                        className="monthly-projection-month-button"
                        aria-label={`Selecionar ${month.label}`}
                        aria-current={month.key === focusKey ? "true" : undefined}
                        onClick={() => onFocusMonth?.(month.key)}
                      >
                        {month.label}
                      </button>
                    </td>
                    {SERIES.map((definition) => (
                      <td key={definition.key}>{cellText(month.values[definition.key])}</td>
                    ))}
                    {showDifferences && COMPARISONS.map((comparison) => (
                      <td key={comparison.key}>
                        {cellText(computeDifference(month.values[comparison.minuend], month.values[comparison.subtrahend]))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="monthly-projection-table-note">
            “—” = sem valor informado para o mês. A pagar é saldo aberto por vencimento na última carga, não saldo histórico. Não há total geral: valores indisponíveis impediriam um total completo.
          </p>
          <p className="monthly-projection-table-note">{describeToPayPeak(findToPayPeak(model))}</p>
        </section>
      )}

      {focusKey && (
        <MonthlyProjectionInvestigation
          monthKey={focusKey}
          wbs={item?.wbs_code_id ?? ""}
          typeLabel={typeLabel}
          investigations={investigations}
          onInvestigate={onInvestigate}
        />
      )}
    </section>
  );
};

MonthlyProjectionComparison.propTypes = {
  series: PropTypes.object,
  window: PropTypes.shape({
    beginAt: PropTypes.instanceOf(Date),
    endAt: PropTypes.instanceOf(Date),
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  item: PropTypes.object,
  appropriationLevel: PropTypes.string,
  sourceMetadata: PropTypes.object,
  measurementReference: PropTypes.string,
  focusMonth: PropTypes.string,
  onFocusMonth: PropTypes.func,
  hiddenSeries: PropTypes.arrayOf(PropTypes.string),
  onToggleSeries: PropTypes.func,
  onFilterMonth: PropTypes.func,
  investigations: PropTypes.object,
  onInvestigate: PropTypes.func,
  now: PropTypes.instanceOf(Date),
};

MonthlyProjectionComparison.defaultProps = {
  series: {},
  window: undefined,
  loading: false,
  error: null,
  item: null,
  appropriationLevel: undefined,
  sourceMetadata: null,
  measurementReference: null,
  focusMonth: null,
  onFocusMonth: undefined,
  hiddenSeries: [],
  onToggleSeries: undefined,
  onFilterMonth: undefined,
  investigations: {},
  onInvestigate: undefined,
  now: undefined,
};

export default MonthlyProjectionComparison;
